import { format, isToday, isYesterday, isThisYear, formatDuration, differenceInCalendarDays } from 'date-fns';
import { TFunction } from 'i18next';
import { getLocale } from '@/components/common/translateFunctions';

export function dateYMD(date: Date): string {
  return format(date, 'yyyyMMdd');
}

export function date(date: Date, translateFunctions: TFunction): string {
  const patternYMD = translateFunctions('general.dateFormat.YMD');
  return format(date, patternYMD);
}

export function isValidDate(date: Date): boolean {
  return !isNaN(date.getTime());
}

export function dateDot(dateFrom: Date, dateTo: Date, translateFunctions: TFunction): string {
  const patternYMD = translateFunctions('general.dateFormat.YMD');
  if (dateFrom.getFullYear() === dateTo.getFullYear()) {
    if (dateFrom.getMonth() === dateTo.getMonth() && dateFrom.getDate() === dateTo.getDate()) {
      return format(dateFrom, patternYMD);
    }
    const patternMD = translateFunctions('general.dateFormat.MD');
    return format(dateFrom, patternYMD) + '-' + format(dateTo, patternMD);
  } else return format(dateFrom, patternYMD) + '-' + format(dateTo, patternYMD);
}

export function datePeriodMD(dateFrom: Date, dateTo: Date, translateFunctions: TFunction): string {
  const patternYMD = translateFunctions('general.dateFormat.YMD');
  const patternMD = translateFunctions('general.dateFormat.MD');

  return format(dateFrom, patternYMD) + ' - ' + format(dateTo, patternMD);
}

export function dateDistance(from: Date, to: Date, language: string): string {
  const days = differenceInCalendarDays(from, to) + 1;
  const locale = getLocale(language);
  return formatDuration({ days }, { locale });
}

export function fulldate(timeMicros: number, translateFunctions: TFunction): string {
  const patternYMDHMS = translateFunctions('general.dateFormat.YMDHMS');
  return format(new Date(timeMicros / 1000), patternYMDHMS);
}

export function fullDateNoYearNoSeconds(
  timeMicros: number,
  currentLanguage: string,
  translateFunctions: TFunction
): string {
  const patternMDEHM = translateFunctions('general.dateFormat.MDEHM');
  return format(new Date(timeMicros / 1000), patternMDEHM, { locale: getLocale(currentLanguage) });
}

export function fullDateNoYear(timeMicros: number, currentLanguage: string, translateFunctions: TFunction): string {
  const patternMDEHMS = translateFunctions('general.dateFormat.MDEHMS');
  return format(new Date(timeMicros / 1000), patternMDEHMS, { locale: getLocale(currentLanguage) });
}

export function fullDateNoSecond(timeMicros: number, translateFunctions: TFunction): string {
  if (isNaN(timeMicros)) {
    console.log('fullDateNoSecond received NaN');
    return '';
  }
  const patternYMDHM = translateFunctions('general.dateFormat.YMDHM');
  return format(new Date(timeMicros / 1000), patternYMDHM);
}

export function chatdate(timeMicros: number, currentLanguage: string, translateFunctions: TFunction): string {
  const date = new Date(timeMicros / 1000);
  const patternYMDHM = translateFunctions('general.dateFormat.YMDHM');
  const patternHM = translateFunctions('general.dateFormat.HM');
  const patternMDEHM = translateFunctions('general.dateFormat.MDEHM');

  let relTime;
  if (isToday(date)) {
    relTime = translateFunctions('general.dateTime.today') + ', ' + format(date, patternHM);
  } else if (isYesterday(date)) {
    relTime = translateFunctions('general.dateTime.yesterday') + ', ' + format(date, patternHM);
  } else if (isThisYear(date)) {
    relTime = format(date, patternMDEHM, { locale: getLocale(currentLanguage) });
  } else {
    relTime = format(date, patternYMDHM);
  }

  return relTime;
}

export function dateYMDE(date: Date, currentLanguage: string, translateFunctions: TFunction): string {
  const patternYMDE = translateFunctions('general.dateFormat.YMDE');
  return format(date, patternYMDE, { locale: getLocale(currentLanguage) });
}

export function dateMDE(date: Date, currentLanguage: string, translateFunctions: TFunction): string {
  if (!date) return '';
  const patternMDE = translateFunctions('general.dateFormat.MDE');
  return format(date, patternMDE, { locale: getLocale(currentLanguage) });
}

export function datePicker(date: Date, locale: Locale, translateFunctions: TFunction): string {
  const patternYM = translateFunctions('general.dateFormat.YM');
  return format(date, patternYM, { locale });
}
